import { createSlice } from '@reduxjs/toolkit';

import { getConnectionInfoThunk } from '@/store/thunks/poc/connection/getConnectionInfoThunk';
import { initializeP2PStackThunk } from '@/store/thunks/poc/connection/initializeP2PStackThunk';
import { extractThunkError } from '@/store/utils';
import type { ConnectionInfo } from '@/types/poc/libp2p';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface SingleConnectionState {
  sixId: string;
  status: ConnectionStatus;
  peerId: string | null;
  connectedPeers: number;
  errorMessage: string | null;
  isP2PStackInitialized: boolean;
  subscribedTopics: string[];
  initializationProgress: {
    libp2p: boolean;
    helia: boolean;
    messaging: boolean;
  };
  connectionDetails: ConnectionInfo[];
  bootstrapPeerIds: string[];
  messageBrokerPeerId: string | null;
}

export interface ConnectionState {
  connections: SingleConnectionState[];
}

export const createInitialConnection = (sixId: string): SingleConnectionState => ({
  sixId,
  status: 'disconnected',
  peerId: null,
  connectedPeers: 0,
  errorMessage: null,
  isP2PStackInitialized: false,
  subscribedTopics: [],
  initializationProgress: {
    libp2p: false,
    helia: false,
    messaging: false,
  },
  connectionDetails: [],
  bootstrapPeerIds: [],
  messageBrokerPeerId: null,
});

const initialState: ConnectionState = {
  connections: [],
};

const findConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState | undefined =>
  connections.find((connection) => connection.sixId === sixId);

const ensureConnection = (
  connections: SingleConnectionState[],
  sixId: string
): SingleConnectionState[] =>
  findConnection(connections, sixId) == null
    ? [...connections, createInitialConnection(sixId)]
    : connections;

const updateConnection = (
  connections: SingleConnectionState[],
  sixId: string,
  updater: (connection: SingleConnectionState) => SingleConnectionState
): SingleConnectionState[] =>
  connections.map((connection) => (connection.sixId === sixId ? updater(connection) : connection));

const getStatusFromConnections = (
  connectionDetails: ConnectionInfo[],
  isP2PStackInitialized: boolean,
  bootstrapPeerIds: string[],
  messageBrokerPeerId: string | null
): ConnectionStatus => {
  if (!isP2PStackInitialized) return 'disconnected';
  if (connectionDetails.length === 0) return 'connecting';

  const connectedPeerIds = new Set(connectionDetails.map((c) => c.peerId));

  const bootstrapConnected =
    bootstrapPeerIds.length > 0 && bootstrapPeerIds.some((id) => connectedPeerIds.has(id));

  const brokerConnected = messageBrokerPeerId != null && connectedPeerIds.has(messageBrokerPeerId);

  return bootstrapConnected && brokerConnected ? 'connected' : 'connecting';
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeP2PStackThunk.pending, (state, action) => {
        const { sixId } = action.meta.arg;
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          status: 'connecting',
          errorMessage: null,
        }));
      })
      .addCase(initializeP2PStackThunk.fulfilled, (state, action) => {
        state.connections = updateConnection(
          state.connections,
          action.payload.sixId,
          (connection) => ({
            ...connection,
            peerId: action.payload.peerId,
            connectedPeers: action.payload.peers,
            connectionDetails: action.payload.connectionDetails,
            errorMessage: null,
            isP2PStackInitialized: true,
            subscribedTopics: action.payload.subscribedTopics,
            bootstrapPeerIds: action.payload.bootstrapPeerIds,
            messageBrokerPeerId: action.payload.messageBrokerPeerId,
            initializationProgress: {
              libp2p: true,
              helia: true,
              messaging: action.payload.messagingInitialized,
            },
            status: getStatusFromConnections(
              action.payload.connectionDetails,
              true,
              action.payload.bootstrapPeerIds,
              action.payload.messageBrokerPeerId
            ),
          })
        );
      })
      .addCase(initializeP2PStackThunk.rejected, (state, action) => {
        const { sixId } = action.meta.arg;
        const { message } = extractThunkError(action.payload);
        state.connections = ensureConnection(state.connections, sixId);
        state.connections = updateConnection(state.connections, sixId, (connection) => ({
          ...connection,
          status: 'error',
          errorMessage: message ?? action.error.message ?? 'Unknown error',
          isP2PStackInitialized: false,
          initializationProgress: {
            libp2p: false,
            helia: false,
            messaging: false,
          },
        }));
      })
      .addCase(getConnectionInfoThunk.fulfilled, (state, action) => {
        state.connections = ensureConnection(state.connections, action.payload.sixId);
        state.connections = updateConnection(
          state.connections,
          action.payload.sixId,
          (connection) => ({
            ...connection,
            connectionDetails: action.payload.connections,
            connectedPeers: action.payload.connections.length,
            status: getStatusFromConnections(
              action.payload.connections,
              connection.isP2PStackInitialized,
              connection.bootstrapPeerIds,
              connection.messageBrokerPeerId
            ),
          })
        );
      });
  },
});

export default connectionSlice.reducer;
